import { ButtonBase, CircularProgress } from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { memo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import BaseTooltip from '../ui/tooltips/BaseTooltip';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Pagination } from 'swiper/modules';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import { BuildingIcon } from 'components/styledIcons';

const DashboardObjectsSlider = ({
  setObjectsError = () => {},
  setFilterData = () => {},
  setIsFilterLoading = () => {},
}) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedObjectsId, setSelectedObjectsId] = useState([]);
  const [hasError, setHasError] = useState(false);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: 'objects',
    queryFn: async function () {
      const response = await axiosPrivate.get('/admin/object/objects');
      return response.data.data;
    },
    enabled: !hasError,
    onSuccess: (data) => {
      setFilterData(data);
    },
    onError: (error) => {
      setHasError(true);
      setObjectsError(true);
    },
    retry: false,
  });

  const handleObjectId = (objectId) => {
    let entries = Object.fromEntries(searchParams);
    Object.keys(entries).forEach((item) => {
      if (item && item.includes('object')) {
        searchParams.delete(item);
      }
    });
    if (isSelected(objectId)) {
      selectedObjectsId.splice(selectedObjectsId.indexOf(objectId), 1);
    } else {
      selectedObjectsId.push(objectId);
    }
    selectedObjectsId.forEach((item, index) => {
      searchParams.set(`object[${index}]`, item);
    });
    setSearchParams(searchParams);
    setSelectedObjectsId(selectedObjectsId);
  };

  const isSelected = (objectId) => {
    return selectedObjectsId.includes(objectId);
  };

  const setDefaultObjects = () => {
    let entries = Object.fromEntries(searchParams);
    let newObjectsId = [];
    Object.keys(entries).forEach((item) => {
      if (item && item.includes('object')) {
        newObjectsId.push(parseInt(searchParams.get(item)));
      }
    });
    setSelectedObjectsId(newObjectsId);
  };

  useEffect(() => {
    setDefaultObjects();
  }, [searchParams]);

  useEffect(() => {
    setIsFilterLoading(isLoading || isFetching);
  }, [isLoading, isFetching]);

  return (
    <div className='dashboard-objects-slider-wrapper'>
      {isLoading || isFetching ? (
        <div className='circular-progress-box h-[100px]'>
          <CircularProgress size={25} />
        </div>
      ) : data && data?.objects && data?.objects.length > 0 ? (
        <Swiper
          slidesPerView={1}
          spaceBetween={30}
          freeMode={true}
          // pagination={{
          // 	clickable: true
          // }}
          pagination={false}
          breakpoints={{
            640: {
              slidesPerView: 3,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 4,
              spaceBetween: 25,
            },
            1024: {
              slidesPerView: 6,
              spaceBetween: 20,
            },
          }}
          modules={[FreeMode, Pagination]}
        >
          {data?.objects.map((objectItem) => {
            const sel = isSelected(objectItem?.id);
            return (
              <SwiperSlide key={objectItem?.id}>
                <ButtonBase
                  id={`object-${objectItem?.id}`}
                  className={`object-item !shadow-none !p-3 !max-h-[149px] !h-[149px] !border !rounded-lg ${
                    sel
                      ? 'item-selected !bg-base-color !text-white !border-base-color'
                      : '!bg-white !border-line hover:!bg-primary-50'
                  }`}
                  onClick={() => handleObjectId(objectItem?.id)}
                >
                  <div className='object-item-body !p-0 w-full text-left'>
                    {/* Sarlavha: ikonka + nom */}
                    <div className='flex items-center gap-2 mb-1.5'>
                      <div
                        className={`${
                          sel ? 'bg-white/20' : 'bg-primary-50'
                        } p-1.5 rounded-lg shrink-0`}
                      >
                        <BuildingIcon
                          color={sel ? 'white' : 'var(--base-color)'}
                        />
                      </div>
                      <BaseTooltip
                        title={objectItem?.name}
                        arrow={true}
                        placement='top'
                        enterDelay={500}
                      >
                        <span
                          className={`text-[13px] font-medium truncate block min-w-0 flex-1 ${
                            sel ? '!text-white' : '!text-ink'
                          }`}
                        >
                          {objectItem?.name}
                        </span>
                      </BaseTooltip>
                    </div>

                    {/* Katta son: xonadonlar soni */}
                    <div
                      className={`text-[22px] font-semibold leading-none mb-2 ${
                        sel ? 'text-white' : 'text-ink'
                      }`}
                    >
                      {objectItem.total}
                      <span
                        className={`text-[11px] font-normal ml-1 ${
                          sel ? 'text-white/70' : 'text-muted'
                        }`}
                      >
                        {t('common.filter.homesCount')}
                      </span>
                    </div>

                    {/* 2 qator — label 1fr (o'raladi), qiymat auto (o'ngga tekis) */}
                    <div className='grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5'>
                      <span
                        className={`text-[12px] ${
                          sel ? 'text-white/80' : 'text-muted'
                        }`}
                      >
                        {t('common.filter.emptyHomesCount')}
                      </span>
                      <span
                        className={`text-[12px] font-medium text-right tabular-nums ${
                          sel ? 'text-white' : 'text-ink'
                        }`}
                      >
                        {objectItem.active}
                      </span>
                      <span
                        className={`text-[12px] ${
                          sel ? 'text-white/80' : 'text-muted'
                        }`}
                      >
                        {t('common.filter.orders')}
                      </span>
                      <span
                        className={`text-[12px] font-medium text-right tabular-nums ${
                          sel ? 'text-white' : 'text-ink'
                        }`}
                      >
                        {objectItem.order}
                      </span>
                    </div>
                  </div>
                </ButtonBase>
              </SwiperSlide>
            );
          })}
        </Swiper>
      ) : (
        <div className='my-6'>
          <span className='no-data-found-wrapper'>
            <i className='bi bi-exclamation-octagon text-xl mr-1 leading-3' />{' '}
            {t('common.global.noBlocksFound')}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(DashboardObjectsSlider);
